import { useMemo } from 'react';
import { cn } from '../lib/cn.ts';
import { encodeQr, qrPath } from '../lib/qr.ts';
import type { QrEcc } from '../lib/qr.ts';

export interface QrCodeProps {
  /** What the code holds, usually a URL. */
  value: string;
  /** What it is, for screen readers: "QR code for the hub address". */
  label: string;
  /** Size and spacing classes, e.g. 'size-40'. */
  className?: string;
  ecc?: QrEcc;
  /** Dims the code (and says so) while the value is not usable yet. */
  muted?: boolean;
}

/**
 * A QR code, always dark on light whatever the theme (cameras read that best): the code sits
 * in a light-theme island, so it uses the light tokens and no literal colours.
 */
export function QrCode({ value, label, className, ecc = 'M', muted }: QrCodeProps) {
  const q = useMemo(() => {
    try {
      return encodeQr(value, ecc);
    } catch {
      return null;
    }
  }, [value, ecc]);
  const margin = 4;
  const box = (q?.size ?? 21) + margin * 2;
  return (
    <div data-theme="light" className={cn('inline-flex shrink-0 rounded-card bg-surface p-1 text-fg ring-1 ring-border', muted && 'opacity-40', className)}>
      {q ? (
        <svg viewBox={`0 0 ${box} ${box}`} role="img" aria-label={label} className="size-full" shapeRendering="crispEdges">
          <path d={qrPath(q, margin)} fill="currentColor" />
        </svg>
      ) : (
        <span role="img" aria-label={`${label}: too long to show`} className="size-full" />
      )}
    </div>
  );
}
